import type { ApiResponse, HeaderImage } from './api'
import { getPublicApiHeaders } from './public-kb-host'

export type PublicWorkspace = {
  name: string
  slug: string
  logo: string | null
  brandColor: string
  headerImage: HeaderImage | null
  hidePoweredBy: boolean
}

export type PublicCollectionSummary = {
  title: string
  slug: string
  articleCount: number
  authorCount: number
}

export type PublicArticleSummary = {
  title: string
  slug: string
  collectionTitle: string
  updatedAt: string
  snippet: string | null
}

export type PublicAuthor = {
  id: string
  name: string
  image: string | null
}

export type PublicArticleListItem = {
  title: string
  slug: string
  updatedAt: string
}

export type PublicNestedCollection = {
  title: string
  slug: string
  articles: PublicArticleListItem[]
  nestedCollections: PublicNestedCollection[]
}

export type PublicCollectionDetail = {
  title: string
  slug: string
  articleCount: number
  authorCount: number
  authors: PublicAuthor[]
  directArticles: PublicArticleListItem[]
  nestedCollections: PublicNestedCollection[]
}

export type PublicArticle = {
  title: string
  slug: string
  content: Record<string, unknown>
  collectionSlug: string
  collectionTitle: string
  updatedAt: string
  author: PublicAuthor
}

async function publicKbFetch<T>(path: string): Promise<ApiResponse<T>> {
  const response = await fetch(path, {
    headers: {
      'Content-Type': 'application/json',
      ...getPublicApiHeaders(),
    },
  })

  return response.json() as Promise<ApiResponse<T>>
}

export function getPublicWorkspace() {
  return publicKbFetch<PublicWorkspace>('/api/public/knowledge/workspace')
}

export function getPublicCollections() {
  return publicKbFetch<PublicCollectionSummary[]>('/api/public/knowledge/collections')
}

export function getPublicCollection(collectionSlug: string) {
  return publicKbFetch<PublicCollectionDetail>(
    `/api/public/knowledge/collections/${encodeURIComponent(collectionSlug)}`,
  )
}

export function getPublicArticle(articleSlug: string) {
  return publicKbFetch<PublicArticle>(
    `/api/public/knowledge/articles/${encodeURIComponent(articleSlug)}`,
  )
}

export function searchPublicArticles(query: string) {
  const params = new URLSearchParams({ q: query })
  return publicKbFetch<PublicArticleSummary[]>(`/api/public/knowledge/search?${params.toString()}`)
}
